import { SensorData } from '../types';
import { supabase } from './supabaseClient';

// --- SUPABASE INTEGRATION ---

export const validateUserPin = async (secretId: string, pin: string): Promise<boolean> => {
  if (supabase) {
    try {
      // Server-side check via RPC — the pin column itself is not readable by the
      // anon key (see supabase_migrations/003_pin_rate_limit_and_security.sql).
      // The RPC also enforces rate limiting (5 fails/min -> locked out).
      const { data, error } = await supabase.rpc('validate_pin', {
        p_secret_id: secretId,
        p_pin: pin,
      });

      if (error) {
        console.error('Pin Validation Error', error);
        return false;
      }
      return data === true;
    } catch (e) {
      console.error("Pin Validation Error", e);
      return false;
    }
  }
  // Fallback
  return pin === '1234';
};

export interface FetchResult {
  sensors: SensorData[];
  isLive: boolean;
  customerName?: string;
  // Set when the real data could not be loaded. Fabricating a plausible sensor
  // in that case is worse than showing nothing: a customer would be looking at
  // invented readings with no way to tell.
  error?: string;
}

// One row per page load, not per manual refresh or per the several queries a
// load triggers underneath — those are Supabase request volume, already
// counted on Supabase's own Usage page. Fire-and-forget: a visit log failing
// should never block the dashboard from loading.
export const logDashboardVisit = (secretId: string) => {
  if (!supabase) return;
  supabase.rpc('log_dashboard_visit', { p_secret_link_id: secretId })
    .then(({ error }) => { if (error) console.error('Visit Log Error', error); });
};

export const fetchUserData = async (secretId: string): Promise<FetchResult> => {
  if (supabase) {
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, name')
        .eq('secret_link_id', secretId)
        .single();

      if (userData && !userError) {
        const { data: sensors, error: sensorError } = await supabase
          .from('sensors')
          .select(`
            id, name, alias, type, role, last_seen,
            readings ( readings, created_at )
          `)
          .eq('user_id', userData.id)
          .order('created_at', { foreignTable: 'readings', ascending: false })
          .limit(20, { foreignTable: 'readings' });

        if (!sensorError && sensors) {
          // A gateway has a second row for its own health telemetry, owned by
          // the same customer. It is an admin diagnostic, not something they
          // bought — showing it put a card full of heap and reset reasons next
          // to their water usage. Filtered here rather than in the query so a
          // legacy row with no role still comes through.
          // Sorted here because the query asks for no order, and Postgres then
          // returns rows in whatever order is convenient — which shifts each
          // time a sensor reports and its row is rewritten, so the cards
          // swapped places on every refresh.
          const rank = (s: any) => (s.role === 'gateway' ? 0 : 1);
          const label = (s: any) => String(s.alias ?? s.name ?? '');
          const customerFacing = sensors
            .filter((s: any) => s.role !== 'telemetry' && s.type !== 'hub')
            .sort((a: any, b: any) => rank(a) - rank(b) || label(a).localeCompare(label(b)));
          return { sensors: transformSupabaseData(customerFacing), isLive: true, customerName: userData.name };
        }
      }
      return {
        sensors: [],
        isLive: false,
        error: 'We could not load your readings just now.',
      };
    } catch (error) {
      console.warn("Supabase fetch failed", error);
      return {
        sensors: [],
        isLive: false,
        error: 'We could not load your readings just now.',
      };
    }
  }

  // No Supabase configured at all — this is a local/demo build, not a failure,
  // so sample data is honest here in a way it never is for a real customer.
  await new Promise(resolve => setTimeout(resolve, 800));
  const now = new Date();
  const mockSensors: SensorData[] = [
    {
      id: 'sensor-001',
      name: 'Living Room (Demo)',
      type: 'multi',
      lastSeen: 'Just now',
      values: [
        {
          key: 'temperature', label: 'Temperature', current: 22.5, unit: '°C',
          history: Array.from({length: 10}, (_, i) => ({ timestamp: new Date(now.getTime() - i * 3600000).toISOString(), value: 20 + Math.random() * 5 })).reverse()
        },
        {
          key: 'humidity', label: 'Humidity', current: 45, unit: '%', history: [] }
      ]
    }
  ];

  return { sensors: mockSensors, isLive: false };
};

const FIELD_UNITS: Record<string, string> = {
  temperature: '°C',
  humidity: '%',
  power: 'kW',
  voltage: 'V',
  current: 'A',
  flow_lpm: 'L/min',
  total_liters: 'L',
  accumulator: 'L',
  level_pct: '%',
  level_mm: 'mm',
  pressure: 'bar',
  // LoRa nodes report battery in centivolts; the WiFi devices report volts.
  battery_cV: 'cV',
  battery_v: 'V',
  angle_deg: '°',
};

// Fields that exist for the system, not for the customer. A relayed LoRa
// reading carries the frame's own bookkeeping alongside the measurement, and
// without this filter the dashboard renders "Msg Type", "Counter", "Rssi" and
// "Relayed By" as if they were things the customer bought a sensor to see.
const INTERNAL_FIELDS = new Set([
  'msg_type', 'counter', 'rssi', 'relayed_by', 'status_code', 'trigger',
  'mac', 'firmware_version', 'fw_version', 'free_heap_kb', 'reset_reason', 'uptime_s',
  'buffer_entries', 'setup_ap_active', 'wifi_rssi', 'lora_packets_relayed',
]);

function transformSupabaseData(sensors: any[]): SensorData[] {
  return sensors.map(s => {
    const latestReading = s.readings?.[0]?.readings || {};
    const historyData = s.readings || [];

    // Build one value card per field in the payload
    const values = Object.keys(latestReading)
      // Numbers only. A single non-numeric field used to throw inside
      // buildValue and take the whole fetch down with it -- the hub's
      // "on_mains": true was enough to blank a customer's dashboard.
      .filter(key => !INTERNAL_FIELDS.has(key) && typeof latestReading[key] === 'number')
      .map(key => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return buildValue(key, label, latestReading[key], FIELD_UNITS[key] || '', historyData);
      });

    return {
      // alias is what this customer calls this unit; name is the product, which
      // every unit shares. Showing name alone turned three distinct meters into
      // three rows all called "M3TRmon".
      id: s.id, name: s.alias || s.name, type: s.type as any,
      lastSeen: s.last_seen ? new Date(s.last_seen).toLocaleString() : 'Never',
      values: values.length > 0 ? values : [buildValue('value', 'Value', 0, '', historyData)]
    };
  });
}

function buildValue(key: string, label: string, current: unknown, unit: string, historyRaw: any[]) {
  const n = Number(current);
  return {
    key,
    label,
    current: Number.isFinite(n) ? Number(n.toFixed(1)) : 0,
    unit,
    history: historyRaw.map((h: any) => ({
      timestamp: h.created_at,
      value: Number(h.readings?.[key]) || 0
    })).reverse()
  };
}

export interface ConsumptionPoint {
  label: string;
  liters: number;
  offline: boolean;  // a reporting gap fell within this period — liters is a floor, not the true total
  // Mean of the readings that fell in this period, or null where the device
  // sends no temperature at all. Null rather than 0 so the line breaks instead
  // of diving to freezing.
  tempC?: number | null;
}

interface FlowPoint {
  created_at: string;
  flow_lpm: number;
  // Monotonic litres since the device booted. M3TRmon (WiFi) calls it
  // total_liters, a relayed LoRa node calls it accumulator — same quantity,
  // both RAM-only and both restarting at 0 on reboot. Undefined on readings
  // that predate it, which is why the rate-based path below still exists.
  accum?: number;
  // The device's own message counter. Two hubs relaying the same LoRa packet
  // produce two rows carrying an identical counter and accumulator; that pair
  // identifies a duplicate exactly, with no guessing.
  counter?: number;
  temperature?: number;   // absent on devices with no probe fitted
}

// Longest gap that still counts as the device working normally.
//
// This tracked a fixed 60s reporting interval until firmware v25 made reporting
// adaptive: every 6h while idle, and only 5/10/30/60 min while water is
// actually flowing. The old 5-minute threshold meant every ordinary idle
// interval looked like an outage, so real consumption was thrown away and
// almost every bar got flagged as incomplete.
//
// Integrating over a long interval is safe here because flow_lpm is the
// *average* rate for the interval it covers, not an instantaneous sample — so
// rate x duration is the exact volume regardless of how long the interval is.
// Keep this above the firmware's idle interval (FLOW_IDLE_REPORT_INTERVAL_MS)
// or normal operation reads as downtime again.
const IDLE_REPORT_INTERVAL_MS = 6 * 60 * 60 * 1000;   // firmware v25+
const OFFLINE_GAP_THRESHOLD_MS = IDLE_REPORT_INTERVAL_MS + 60 * 60 * 1000;  // 7h

// Paginated on purpose. PostgREST caps an unbounded select at 1000 rows, and
// this query asked for them ascending — so once a sensor had more than 1000
// readings in the range, what came back was the OLDEST 1000 and everything
// recent vanished without an error. A device reporting every 5 minutes while
// water runs passes that in days, and the symptom is a dashboard that quietly
// stops moving forward while looking perfectly healthy.
//
// The real fix for long ranges is a server-side daily rollup; this keeps the
// numbers correct until that exists.
const PAGE = 1000;
const MAX_ROWS = 20000;   // ~70 days of continuous 5-minute reporting

async function fetchFlowSeries(sensorId: string, fromISO: string): Promise<FlowPoint[]> {
  if (!supabase) return [];

  const rows: any[] = [];
  for (let from = 0; from < MAX_ROWS; from += PAGE) {
    const { data: page, error: pageError } = await supabase
      .from('readings')
      .select('readings, created_at')
      .eq('sensor_id', sensorId)
      .gte('created_at', fromISO)
      .order('created_at', { ascending: true })
      .range(from, from + PAGE - 1);

    if (pageError) { console.error('Flow series fetch error', pageError); break; }
    if (!page || page.length === 0) break;
    rows.push(...page);
    if (page.length < PAGE) break;
  }

  const data = rows;
  const error = null;
  if (error || !data) return [];

  const mapped = data
    .filter((r: any) => typeof r.readings?.flow_lpm === 'number')
    .map((r: any) => ({
      created_at: r.created_at,
      flow_lpm: r.readings.flow_lpm,
      accum: typeof r.readings?.total_liters === 'number' ? r.readings.total_liters
           : typeof r.readings?.accumulator  === 'number' ? r.readings.accumulator
           : undefined,
      counter: typeof r.readings?.counter === 'number' ? r.readings.counter : undefined,
      // -127 is the protocol's "no probe fitted" sentinel (TEMP_NOT_PRESENT_x10
      // in the node firmware), not a reading. Taken literally it plots a line
      // at -127 C and drags the axis with it. Anything below -50 is a sentinel
      // or a fault: water doesn't get there.
      temperature: typeof r.readings?.temperature === 'number' && r.readings.temperature > -50
        ? r.readings.temperature : undefined,
    }));

  // Drop exact duplicates, keeping the first -- which, being the earliest
  // created_at, is also the copy that arrived on time.
  //
  // Any hub in earshot relays a LoRa packet, and ingest's dedup window is
  // anchored to now rather than to the reading's own time, so a hub uploading a
  // backlog slips copies past it hours later. Those land with an accumulator
  // lower than the readings around them, and the consumption maths downstream
  // then has to decide whether a drop means a duplicate or a reboot. It cannot:
  // the two are indistinguishable in the accumulator alone, and every rule that
  // separates them trades inventing litres for losing them. Removing the
  // duplicates here -- which (counter, accumulator) identifies exactly -- means
  // the only drops left are real restarts.
  const seen = new Set<string>();
  return mapped.filter(p => {
    if (p.counter === undefined || p.accum === undefined) return true;
    const key = `${p.counter}:${p.accum}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Turns a reading series into litres-per-interval ("partial values").
//
// Primary method: differences of the device's own accumulator. It is monotonic
// while the device stays up, so consumption over any span is (last - first) and
// nothing else matters -- lose fifty messages in a row and the total is still
// exact, because only the endpoints are read. That also makes it immune to two
// defects the rate-based method below cannot escape:
//
//   - Buffered readings are stamped with created_at when the buffer is flushed,
//     not when they were measured. Measured 2026-09-30: 34% of this fleet's
//     readings arrive less than 5s apart, having really been taken ~60s apart.
//     Anything multiplying a rate by that delta mis-attributes the volume.
//   - flow_lpm is the average over the window it covers, so the first report
//     after a long idle spell is diluted across hours. Correct for volume, but
//     only because the multiplication below happens to invert it exactly.
//
// A reboot resets the accumulator to zero (it is RAM-only on both firmwares, by
// a deliberate 2026-08-27 decision -- a persisted one desyncs forever after a
// power cut). So the rule is simply: if it went down, that is a new session.
// The litres between a session's last message and the reboot are unrecoverable;
// that loss is inherent and small.
//
// Fallback: readings with no accumulator (anything before it was sent) still
// use rate x elapsed, with a gap bigger than OFFLINE_GAP_THRESHOLD_MS
// contributing zero rather than fabricating a number from an unknown rate.
function computePartialVolumes(points: FlowPoint[]): { volumes: { created_at: string; liters: number }[]; gapEnds: Set<string> } {
  const volumes: { created_at: string; liters: number }[] = [];
  const gapEnds = new Set<string>();

  // The accumulator of the last reading we accepted -- not simply the previous
  // element. A reading rejected as out-of-order must not become the baseline
  // for the next one, or its lower value inflates the following interval by
  // exactly the amount the rejection was meant to avoid.
  let lastAccum: number | undefined;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const deltaMs = new Date(curr.created_at).getTime() - new Date(prev.created_at).getTime();
    if (deltaMs <= 0) continue;

    if (lastAccum === undefined && prev.accum !== undefined) lastAccum = prev.accum;

    if (lastAccum !== undefined && curr.accum !== undefined) {
      if (curr.accum >= lastAccum) {
        volumes.push({ created_at: curr.created_at, liters: curr.accum - lastAccum });
        lastAccum = curr.accum;
        continue;
      }

      // The accumulator went backwards. Two very different things do that, and
      // treating them alike credited a whole lifetime accumulator as a single
      // interval's consumption:
      //
      //   A reboot -- the counter restarts near zero and climbs from there, so
      //   every later reading continues from the low value.
      //
      //   A reading that arrived late -- two hubs hear the same node and one
      //   uploads hours afterwards, past ingest's dedup window, which is
      //   anchored to now rather than to the reading's own time. It carries an
      //   older, lower accumulator while the series around it is unaffected.
      //
      // Exact duplicates are already gone (see fetchFlowSeries), so a drop here
      // is a restart. One guard remains for a reading that arrived late without
      // being a duplicate: if the very next one is straight back where the
      // series was, nothing restarted.
      let next: FlowPoint | undefined;
      for (let j = i + 1; j < points.length; j++) {
        if (points[j].accum !== undefined) { next = points[j]; break; }
      }

      if (next === undefined || next.accum! >= lastAccum) {
        // An isolated dip, or nothing left to judge by: treat it as having
        // arrived late. Its volume is already covered by the readings on either
        // side, so it contributes nothing and -- importantly -- does not become
        // the baseline. Billing a customer for water they did not use is the
        // worse error, so the ambiguous final reading lands here too, flagged,
        // since a genuine reboot at the very end loses its litres this way.
        if (next === undefined) gapEnds.add(curr.created_at);
        continue;
      }

      // The drop persisted: a real restart. What it counted since booting is
      // what we can still claim; whatever it measured before the reboot and
      // never sent is gone, which makes this period a floor rather than a
      // total, hence the flag. An offline stretch the accumulator spanned does
      // not get it, because that number is exact.
      volumes.push({ created_at: curr.created_at, liters: curr.accum });
      lastAccum = curr.accum;
      gapEnds.add(curr.created_at);
      continue;
    }

    if (deltaMs > OFFLINE_GAP_THRESHOLD_MS) {
      gapEnds.add(curr.created_at);
      continue;
    }

    volumes.push({ created_at: curr.created_at, liters: curr.flow_lpm * (deltaMs / 60000) });
  }

  return { volumes, gapEnds };
}

// Today, hour by hour. Reads back a day further than midnight on purpose: the
// interval that straddles midnight has to be measured from the reading before
// it, and without that earlier point the first hour of the day would look empty.
// Rolling last 60 minutes, not "the current hour bucket" — at 13:05 the bucket
// holds five minutes and reads as a collapse in usage. Looks back further than
// the hour so the interval crossing the boundary has a previous point to
// measure from.

// Mean temperature per bucket, averaged over the readings themselves rather
// than the computed volumes: a temperature belongs to the moment it was
// sampled, whether or not any water moved. Buckets with no reading stay absent
// so the caller can emit null and let the line break instead of diving to zero.
function meanTempByBucket(
  points: FlowPoint[], bucketOf: (d: Date) => number, include: (d: Date) => boolean,
): Map<number, number> {
  const acc = new Map<number, { sum: number; n: number }>();
  points.forEach(p => {
    if (p.temperature === undefined) return;
    const d = new Date(p.created_at);
    if (!include(d)) return;
    const k = bucketOf(d);
    const a = acc.get(k) ?? { sum: 0, n: 0 };
    a.sum += p.temperature;
    a.n += 1;
    acc.set(k, a);
  });
  const out = new Map<number, number>();
  acc.forEach((a, k) => out.set(k, Number((a.sum / a.n).toFixed(1))));
  return out;
}

export const fetchLastHourTotal = async (sensorId: string): Promise<number> => {
  const since = new Date(Date.now() - OFFLINE_GAP_THRESHOLD_MS - 60 * 60 * 1000).toISOString();
  const points = await fetchFlowSeries(sensorId, since);
  const { volumes } = computePartialVolumes(points);
  const cutoff = Date.now() - 60 * 60 * 1000;
  const total = volumes
    .filter(v => new Date(v.created_at).getTime() >= cutoff)
    .reduce((sum, v) => sum + v.liters, 0);
  return Number(total.toFixed(1));
};

export const fetchHourlyConsumption = async (sensorId: string): Promise<ConsumptionPoint[]> => {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const lookBack = new Date(startOfDay.getTime() - OFFLINE_GAP_THRESHOLD_MS).toISOString();
  const points = await fetchFlowSeries(sensorId, lookBack);
  const { volumes, gapEnds } = computePartialVolumes(points);

  const byHour = new Map<number, { liters: number; offline: boolean }>();
  const sameDay = (ts: string) => new Date(ts) >= startOfDay;

  const tempByHour = meanTempByBucket(points, d => d.getHours(), d => d >= startOfDay);

  volumes.filter(v => sameDay(v.created_at)).forEach(v => {
    const hour = new Date(v.created_at).getHours();
    const entry = byHour.get(hour) ?? { liters: 0, offline: false };
    entry.liters += v.liters;
    byHour.set(hour, entry);
  });
  Array.from(gapEnds).filter(sameDay).forEach(ts => {
    const hour = new Date(ts).getHours();
    const entry = byHour.get(hour) ?? { liters: 0, offline: false };
    entry.offline = true;
    byHour.set(hour, entry);
  });

  return Array.from({ length: 24 }, (_, h) => {
    const entry = byHour.get(h);
    const t = tempByHour.get(h);
    return {
      label: String(h).padStart(2, '0'),
      liters: entry ? Number(entry.liters.toFixed(1)) : 0,
      offline: entry?.offline ?? false,
      tempC: t ?? null,
    };
  });
};

export const fetchDailyConsumption = async (sensorId: string): Promise<ConsumptionPoint[]> => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const points = await fetchFlowSeries(sensorId, startOfMonth);
  const { volumes, gapEnds } = computePartialVolumes(points);

  const byDay = new Map<number, { liters: number; offline: boolean }>();
  volumes.forEach(v => {
    const day = new Date(v.created_at).getDate();
    const entry = byDay.get(day) ?? { liters: 0, offline: false };
    entry.liters += v.liters;
    byDay.set(day, entry);
  });
  gapEnds.forEach(ts => {
    const day = new Date(ts).getDate();
    const entry = byDay.get(day) ?? { liters: 0, offline: false };
    entry.offline = true;
    byDay.set(day, entry);
  });

  const tempByDay = meanTempByBucket(points, d => d.getDate(), () => true);

  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const entry = byDay.get(day);
    return {
      label: String(day),
      liters: entry ? Number(entry.liters.toFixed(1)) : 0,
      offline: entry?.offline ?? false,
      tempC: tempByDay.get(day) ?? null,
    };
  });
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const fetchMonthlyConsumption = async (sensorId: string): Promise<ConsumptionPoint[]> => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1).toISOString();
  const points = await fetchFlowSeries(sensorId, startOfYear);
  const { volumes, gapEnds } = computePartialVolumes(points);

  const byMonth = new Map<number, { liters: number; offline: boolean }>();
  volumes.forEach(v => {
    const month = new Date(v.created_at).getMonth();
    const entry = byMonth.get(month) ?? { liters: 0, offline: false };
    entry.liters += v.liters;
    byMonth.set(month, entry);
  });
  gapEnds.forEach(ts => {
    const month = new Date(ts).getMonth();
    const entry = byMonth.get(month) ?? { liters: 0, offline: false };
    entry.offline = true;
    byMonth.set(month, entry);
  });

  const tempByMonth = meanTempByBucket(points, d => d.getMonth(), () => true);

  return MONTH_LABELS.map((label, i) => {
    const entry = byMonth.get(i);
    return {
      label,
      liters: entry ? Number(entry.liters.toFixed(1)) : 0,
      offline: entry?.offline ?? false,
      tempC: tempByMonth.get(i) ?? null,
    };
  });
};

// Lifetime total — sum of partial volumes across every reading ever received,
// computed here rather than trusted from an on-device counter (which goes
// silently wrong forever after a single missed power cycle).
export const fetchLifetimeTotal = async (sensorId: string): Promise<number> => {
  const points = await fetchFlowSeries(sensorId, new Date(0).toISOString());
  const { volumes } = computePartialVolumes(points);
  return Number(volumes.reduce((sum, v) => sum + v.liters, 0).toFixed(1));
};
// --- Customer settings -----------------------------------------------------

export interface SensorAlertSetting {
  sensorId: string;
  deviceId: string;
  alias: string | null;
  enabled: boolean;
  liters: number | null;
  email: string | null;
  lastSentAt: string | null;
}

export interface CustomerSettings {
  billingCutoffDay: number | null;
  showProductHints: boolean;
  alerts: SensorAlertSetting[];
}

export const fetchCustomerSettings = async (secretId: string): Promise<CustomerSettings | null> => {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('get_customer_settings', { p_secret_link_id: secretId });
  if (error || !data || data.length === 0) {
    if (error) console.error('Settings Fetch Error', error);
    return null;
  }
  return {
    billingCutoffDay: data[0].billing_cutoff_day,
    showProductHints: data[0].show_product_hints,
    // A customer with no sensors still returns one row, from the left join.
    alerts: data
      .filter((r: any) => r.sensor_id)
      .map((r: any) => ({
        sensorId: r.sensor_id,
        deviceId: r.device_id,
        alias: r.alias,
        enabled: r.alert_enabled,
        liters: r.alert_liters,
        email: r.alert_email,
        lastSentAt: r.alert_last_sent_at,
      })),
  };
};

export const saveBillingCutoff = async (secretId: string, day: number | null): Promise<string | null> => {
  if (!supabase) return 'Sin conexión.';
  const { error } = await supabase.rpc('set_billing_cutoff', { p_secret_link_id: secretId, p_day: day });
  return error ? error.message : null;
};

export const saveShowProductHints = async (secretId: string, show: boolean): Promise<string | null> => {
  if (!supabase) return 'Sin conexión.';
  const { error } = await supabase.rpc('set_show_product_hints', { p_secret_link_id: secretId, p_show: show });
  return error ? error.message : null;
};

export const saveSensorAlert = async (
  secretId: string, sensorId: string, enabled: boolean, liters: number | null, email: string | null,
): Promise<string | null> => {
  if (!supabase) return 'Sin conexión.';
  const { error } = await supabase.rpc('set_sensor_alert', {
    p_secret_link_id: secretId, p_sensor_id: sensorId,
    p_enabled: enabled, p_liters: liters, p_email: email,
  });
  return error ? error.message : null;
};

// --- Analytics -------------------------------------------------------------

export interface SensorAnalytics {
  periodStart: Date;
  periodEnd: Date;
  usedSoFar: number;       // litres since the period opened
  dailyAverage: number;    // litres per complete day
  projected: number;       // projected total for the whole period
  completeDays: number;    // how many whole days the average rests on
  peakLpm: number | null;  // highest reported rate today
  peakAt: Date | null;
}

// The billing period containing `now`, from a day-of-month cutoff. Months are
// uneven, so a cutoff of 31 has to fall back to the last day that exists —
// which is what a utility does rather than skipping February.
const periodBounds = (cutoffDay: number, now: Date) => {
  const clampDay = (y: number, m: number) =>
    Math.min(cutoffDay, new Date(y, m + 1, 0).getDate());

  const thisMonthCut = new Date(now.getFullYear(), now.getMonth(),
                                clampDay(now.getFullYear(), now.getMonth()));

  if (now >= thisMonthCut) {
    const nY = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
    const nM = (now.getMonth() + 1) % 12;
    return { start: thisMonthCut, end: new Date(nY, nM, clampDay(nY, nM)) };
  }
  const pY = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const pM = (now.getMonth() + 11) % 12;
  return { start: new Date(pY, pM, clampDay(pY, pM)), end: thisMonthCut };
};

export const fetchAnalytics = async (
  sensorId: string, cutoffDay: number,
): Promise<SensorAnalytics | null> => {
  const now = new Date();
  const { start, end } = periodBounds(cutoffDay, now);

  const lookBack = new Date(start.getTime() - OFFLINE_GAP_THRESHOLD_MS).toISOString();
  const points = await fetchFlowSeries(sensorId, lookBack);
  if (points.length === 0) return null;

  const { volumes } = computePartialVolumes(points);
  const inPeriod = volumes.filter(v => {
    const t = new Date(v.created_at);
    return t >= start && t < end;
  });

  const usedSoFar = inPeriod.reduce((t, v) => t + v.liters, 0);

  // Averaged over COMPLETE days only. Today is partial by definition, and
  // including it drags the average down in proportion to how early it is —
  // at 9am a projection would read about a third of the truth.
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const byDay = new Map<string, number>();
  inPeriod.forEach(v => {
    const d = new Date(v.created_at);
    if (d >= startOfToday) return;
    const key = d.toDateString();
    byDay.set(key, (byDay.get(key) ?? 0) + v.liters);
  });

  const completeDays = byDay.size;
  const dailyAverage = completeDays > 0
    ? Array.from(byDay.values()).reduce((a, b) => a + b, 0) / completeDays
    : 0;

  // Remaining whole days after today, each at the running average, plus what
  // today has already used. A mean of daily totals rather than a trend line:
  // with a few weeks of history a regression mostly fits noise, and a
  // household's consumption has no reason to be heading anywhere in
  // particular. Revisit once there's enough history for a weekday profile.
  const msPerDay = 86400000;
  const daysLeft = Math.max(0, Math.ceil((end.getTime() - startOfToday.getTime()) / msPerDay) - 1);
  const projected = usedSoFar + dailyAverage * daysLeft;

  // Peak rate today. flow_lpm is the average over its reporting interval, so a
  // brief surge inside a 5-minute window is already flattened — this is the
  // highest rate actually reported, not a true per-minute maximum.
  let peakLpm: number | null = null;
  let peakAt: Date | null = null;
  points.forEach(p => {
    const t = new Date(p.created_at);
    if (t < startOfToday) return;
    if (peakLpm === null || p.flow_lpm > peakLpm) { peakLpm = p.flow_lpm; peakAt = t; }
  });

  return {
    periodStart: start, periodEnd: end,
    usedSoFar: Number(usedSoFar.toFixed(1)),
    dailyAverage: Number(dailyAverage.toFixed(1)),
    projected: Number(projected.toFixed(1)),
    completeDays,
    peakLpm: peakLpm !== null ? Number((peakLpm as number).toFixed(2)) : null,
    peakAt,
  };
};
